// What a person may do to one workload, and the words for it (02-DESIGN/
// 04-workloads/01-the-workload-model.md § the verbs; 01-the-console.md § the
// words: *Remove* · *Stop* · *Start*; § conventions: a row's actions,
// remove). One rule for the workload's page and its row on the list.

import type { Verb } from "../../roles/offers.ts";
import type { MenuEntry } from "../../ui/table.tsx";
import { PLATFORM_REGISTRANT, RegistrationState, type Workload } from "../../wire/workloads.ts";

/** Acts says which of Stop, Start and Remove a workload offers this person. */
export interface Acts {
  stop: boolean;
  start: boolean;
  remove: boolean;
}

/**
 * workloadActs decides Stop, Start and Remove. A platform workload, a
 * required one and a solution's are never removed here; an admin may stop and
 * start them, except the steward. A removed workload offers nothing.
 */
export function workloadActs(
  w: Workload,
  { manages, offered }: { manages: (subject: string) => boolean; offered: (verb: Verb) => boolean },
): Acts {
  const platforms = w.registrant === PLATFORM_REGISTRANT;
  const required = w.required === true;
  const steward = platforms && w.platform === "steward";
  const mine = manages(w.registrant) && !platforms && !required && w.solution === undefined;
  const stoppable =
    (platforms || required || w.solution !== undefined) && !steward && offered("workloads:suspend");
  const live = (mine || stoppable) && w.state !== RegistrationState.retired;
  return {
    stop: live && offered("workloads:suspend") && w.state === RegistrationState.active && !steward,
    start: live && offered("workloads:suspend") && w.state === RegistrationState.suspended,
    remove: live && mine,
  };
}

/** stopConsequence says what Stop does: a required workload's steward starts it again. */
export function stopConsequence(w: Workload, requiredBy: string): string {
  return w.required === true
    ? `It stops now; ${requiredBy}'s steward starts it again at its next pass.`
    : "It stops now and does not start again until you start it.";
}

export const START_CONSEQUENCE = "A fresh copy starts.";
export const REMOVE_CONSEQUENCE = "For good: it stops and never starts again. It stays listed as removed.";

/** workloadMenu is a workload's row menu: Stop or Start, then Remove after the divider, each asked first. */
export function workloadMenu(
  w: Workload,
  acts: Acts,
  requiredBy: string,
  act: (verb: "retire" | "suspend" | "resume") => void,
): MenuEntry[] {
  const entries: MenuEntry[] = [];
  if (acts.stop) {
    entries.push({
      label: "Stop",
      confirm: { title: `Stop ${w.name}?`, consequence: stopConsequence(w, requiredBy) },
      onSelect: () => {
        act("suspend");
      },
    });
  }
  if (acts.start) {
    entries.push({
      label: "Start",
      confirm: { title: `Start ${w.name}?`, consequence: START_CONSEQUENCE },
      onSelect: () => {
        act("resume");
      },
    });
  }
  if (acts.remove) {
    entries.push({
      label: "Remove",
      danger: true,
      confirm: { title: `Remove workload ${w.name}?`, consequence: REMOVE_CONSEQUENCE },
      onSelect: () => {
        act("retire");
      },
    });
  }
  return entries;
}
